// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import PaginationPresentationPropsDefault from "@insite/mobius/Pagination/presentationProps";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import Next from "@synd/XDL/Icons/LinkArrowRight";
import Previous from "@synd/XDL/Icons/LinkLeftArrow";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import { color, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...PaginationPresentationPropsDefault,
        mergeCss: true,
        selectProps: {
            cssOverrides: {
                formField: css`
                    display: flex;
                    align-items: center;
                    ${xScreen("lte", "sm")} {
                        flex-direction: row;
                        gap: 8px;
                    }
                `,
                formInputWrapper: css`
                    width: auto;
                    max-width: 75px;
                    min-width: 48px;
                    ${xScreen("lte", "sm")} {
                        &&&&& {
                            margin-top: 0;
                        }
                    }
                `,
                inputSelect: css`
                    font-size: 14px;
                `,
            },
            labelProps: {
                mergeCss: true,
                transform: "capitalize",
                weight: fontConfig.weight.regular,
                color: "text",
                css: css`
                    min-height: initial;
                    letter-spacing: 0;
                    line-height: ${fontConfig.smallSingleLine.lineHeight};
                    display: flex;
                    align-items: center;
                    font-size: ${fontConfig.smallSingleLine.size}px;
                    width: fit-content;
                    ${xScreen("lte", "sm")} {
                        white-space: nowrap;
                    }
                `,
            },
        },
        navIconsSrc: {
            firstPage: "ChevronsLeft",
            previousPage: Previous,
            nextPage: Next,
            lastPage: "ChevronsRight",
        },

        buttonProps: {
            mergeCss: true,
            sizeVariant: "small",
            typographyProps: {
                size: fontConfig.xSmall.size,
                css: css`
                    letter-spacing: 0;
                    line-height: ${fontConfig.xSmall.lineHeight};
                `,
            },
            css: css`
                min-height: 18px;
                border: none;
                ${IconWrapper} {
                    color: ${color("common.ui7")};
                }
                &:disabled {
                    background: transparent;
                    ${IconWrapper} {
                        color: ${color("common.ui7", 0.33)};
                    }
                }
                &:hover:not(:disabled) {
                    background: transparent;
                    border: none;
                    color: transparent;
                    ${IconWrapper} {
                        color: ${color("common.ui10")};
                    }
                }
                &:focus {
                    background: transparent;
                }
            `,
        },
        cssOverrides: {
            ...PaginationPresentationPropsDefault?.cssOverrides,
            ellipsis: css`
                margin: 0 10px;
            `,
            linkList: css`
                & > ul {
                    display: flex;
                    align-items: center;
                }
                & > .pagination-container {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    & > .pagination-element {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        & .page-count {
                            white-space: nowrap;
                            text-transform: lowercase;
                        }
                    }
                }
            `,
            pagination: css`
                gap: 32px;
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin: 20px 0;
                                flex-wrap: nowrap;
                            `,
                            css`
                                flex-wrap: nowrap;
                            `,
                            null,
                            css`
                                margin: 20px 0;
                            `,
                            null,
                        ],
                        "min",
                    )};
                margin: 20px 0;
                ${({ justifyContent }: any) => justifyContent && `justify-content: ${justifyContent};`}
            `,
        },
    },
} as xBaseTheme["pagination"];
